'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { usePathname } from 'next/navigation';
import useRoute from '@/ueshook/useRoute';
import { IntroUpload } from '@/components/batchToolsComp/LandingPageIntroUpload/IntroUploadBase';
import { buildRedirectUrl } from './buildRedirectUrl';
export interface MultipleUploaderProps {
  maxImageCount: number;
  nextPageUrlParams?: string;
  asyncBeforeUpload?: () => Promise<void>;
  onImagesUpload?: (files: FileList) => Promise<void>;
}

export function MultipleUploader(props: MultipleUploaderProps) {
  const { maxImageCount } = props;
  const { push } = useRoute();
  const [uploading, setUploading] = useState(false);
  const pathname = usePathname();

  const processUpload = async (files: FileList) => {
    setUploading(true);

    try {
      if (props.onImagesUpload) {
        await props.onImagesUpload(files);
        push(buildRedirectUrl(pathname, props.nextPageUrlParams));
      } else {
        console.error('No image upload handler provided');
        toast.error('Upload handler not configured');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleUpload = async (files: FileList) => {
    if (props?.asyncBeforeUpload) {
      /**
       * @description 此处必须拷贝files, 因为当执行完任何一个promise后, files的数据会丢失
       * @question 为什么执行完promise后files会丢失？ - 目前尚无定论
       */
      const fileListForProcessing = new DataTransfer();
      Array.from(files).forEach((file) =>
        fileListForProcessing.items.add(file)
      );

      await props.asyncBeforeUpload();

      processUpload(fileListForProcessing.files);
    } else {
      processUpload(files);
    }
  };

  const handlePaste = async (file: File) => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    await handleMultipleUpload(dataTransfer.files);
  };

  const handleFileSelect = async (files: FileList) => {
    if (files.length > maxImageCount) {
      toast.error(`You can only upload a maximum of ${maxImageCount} images`);
      return;
    }

    await handleMultipleUpload(files);
  };

  const handleFileDrop = async (files: FileList) => {
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      toast.error('Please upload image files only');
      return;
    }

    if (imageFiles.length > maxImageCount) {
      toast.error(`You can only upload up to ${maxImageCount} images`);
      return;
    }

    const dataTransfer = new DataTransfer();
    imageFiles.forEach((file) => dataTransfer.items.add(file));

    await handleMultipleUpload(dataTransfer.files);
  };

  return (
    <IntroUpload
      maxImageCount={maxImageCount}
      uploading={uploading}
      onFileDrop={handleFileDrop}
      onFileSelect={handleFileSelect}
      onPaste={handlePaste}
      multipleUpload={true}
    />
  );
}
